/**
 * Turns a natural-language query into a sanitised filter object.
 *
 * Network + sanitising live here; the pure helpers live in @/lib/llm.
 */

import api from "@/lib/api";
import {
  extractJsonObject,
  sanitizeFilters,
  JsonExtractionError,
  FILTER_MODEL,
  FILTER_TEMPERATURE,
  FILTER_MAX_TOKENS,
} from "@/lib/llm";

export interface FilterQuerySpec {
  /** Rebuilt per call so date anchors are never stale. */
  buildSystemPrompt: () => string;
  /** Keys the API accepts; anything else the model emits is dropped. */
  allowedKeys: string[];
  /** Optional Anthropic tool input_schema to force structured output. */
  jsonSchema?: Record<string, unknown>;
}

export interface InterpretResult<TFilterResult> {
  filters: TFilterResult;
  /** Hallucinated / unsupported keys that were stripped. */
  droppedKeys: string[];
  /** Number of filter keys that survived sanitising. */
  filterCount: number;
  raw: string;
}

export class FilterInterpretationError extends Error {
  raw?: string;

  constructor(message: string, raw?: string) {
    super(message);
    this.name = "FilterInterpretationError";
    this.raw = raw;
  }
}

export async function interpretFilterQuery<TFilterResult>(
  spec: FilterQuerySpec,
  queryText: string
): Promise<InterpretResult<TFilterResult>> {
  const raw = await api.streamChatCompletion(
    [
      { role: "system", content: spec.buildSystemPrompt() },
      { role: "user", content: queryText },
    ],
    () => {},
    {
      model: FILTER_MODEL,
      temperature: FILTER_TEMPERATURE,
      maxTokens: FILTER_MAX_TOKENS,
      jsonSchema: spec.jsonSchema,
    }
  );

  let parsed: Record<string, unknown>;
  try {
    parsed = extractJsonObject(raw);
  } catch (e) {
    if (e instanceof JsonExtractionError) {
      // Surface the raw text so the failure is diagnosable from the console.
      console.error("Filter parse failed. Raw model response:", e.raw);
      throw new FilterInterpretationError(
        "Couldn't understand the model's answer — try rephrasing.",
        e.raw
      );
    }
    throw e;
  }

  const { filters, droppedKeys, emptyKeys } = sanitizeFilters<TFilterResult>(
    parsed,
    spec.allowedKeys
  );

  if (droppedKeys.length || emptyKeys.length) {
    console.warn("Sentence filter: dropped keys", { droppedKeys, emptyKeys });
  }

  return {
    filters,
    droppedKeys,
    filterCount: Object.keys(filters as Record<string, unknown>).length,
    raw,
  };
}

/** "consent_status: CONSENTED, days_ago: 7" — for the "Interpreted as" line. */
export function describeFilters(filters: unknown): string {
  if (!filters || typeof filters !== "object") return "";
  return Object.entries(filters as Record<string, unknown>)
    .map(([key, value]) => {
      const rendered = Array.isArray(value) ? value.join(",") : String(value);
      return `${key}: ${rendered}`;
    })
    .join(", ");
}
